import {
  CreateMultisigRequest,
  type CreateMyNativeAidRequest
} from '@/api/origin-agent-svc'
import { getOobis } from '@/services/signify'
import {
  findAllNativeAidsFromAgent,
  saveIdentifierToAgent,
  saveMultisigToAgent
} from '@/services/wallet/agent'
import { getIdentifierTypeFromAlias } from '@/services/wallet/util'
import { CLIENT_CONNECTIVITY_STATUS } from '@/state/signify'
import useStore from '@/state/store'
import { useEffect, useState } from 'react'

export enum SyncStatus {
  IDLE = 'IDLE',
  SYNCING = 'SYNCING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}
const useKeriaAgentSync = () => {
  const {
    signifyClient,
    clientConnectivityStatus,
    notificationProcessingStatus,
    getIdentifiers,
    updateIdentifier,
    authentication: { userInfo }
  } = useStore()

  const [status, setStatus] = useState<SyncStatus>(SyncStatus.IDLE)
  const [error, setError] = useState<unknown>(null)

  const sync = async () => {
    // M4.4 Step A: hub-level guards. signifyClient and userInfo are nullable in
    // the store until the user is signed in and the SignifyClient is bootstrapped.
    // The useEffect below already gates on CONNECTED status, so by the time
    // sync() runs these are normally populated — but TS can't see that flow.
    // Treat absent client/user as "not ready, skip this run" rather than throw.
    if (!signifyClient || !userInfo) {
      setStatus(SyncStatus.IDLE)
      return
    }
    const userId = userInfo.id
    const primaryOrgId = userInfo.orgs?.[0]?.id
    if (!userId || !primaryOrgId) {
      setStatus(SyncStatus.IDLE)
      return
    }

    setStatus(SyncStatus.SYNCING)
    try {
      //console.debug('[useKeriaAgentSync] before getIdentifiers')
      const identifiers = await getIdentifiers()
      //console.debug('[useKeriaAgentSync] after getIdentifiers', {
      //  identifiersCount: identifiers?.length ?? 0
      //})
      //console.debug('[useKeriaAgentSync] before findAllNativeAidsFromAgent')
      const agentAids = await findAllNativeAidsFromAgent({
        orgId: primaryOrgId,
        userId: userId
      })
      //console.debug('[useKeriaAgentSync] after findAllNativeAidsFromAgent', {
      //  agentAidsCount: agentAids?.length ?? 0
      //})

      const multisigKeriaIdentifiers = identifiers.filter(
        (identifier) => identifier.isMultisig
      )
      const singleSigKeriaIdentifiers = identifiers.filter(
        (identifier) => !identifier.isMultisig
      )

      for (const identifier of singleSigKeriaIdentifiers) {
        const foundInAgent = agentAids.find(
          (agentAid) => agentAid.aid === identifier.prefix
        )
        let type
        if (!foundInAgent) {
          const oobis = await getOobis(signifyClient, identifier.name, 'agent')
          type = getIdentifierTypeFromAlias(identifier.name, userId)
          const payload: CreateMyNativeAidRequest = {
            aid: identifier.prefix,
            alias: identifier.name,
            oobi: oobis.oobis,
            isMultisig: identifier.isMultisig,
            orgId: primaryOrgId,
            type: type
          }

          try {
            await saveIdentifierToAgent(payload)
          } catch (err) {
            // console.log(err)
          }
        } else {
          type = foundInAgent.type
        }

        const updatedIdentifier = {
          ...identifier,
          type: type
        }
        updateIdentifier(updatedIdentifier)
      }

      for (const identifier of multisigKeriaIdentifiers) {
        const foundInAgent = agentAids.find(
          (agentAid) => agentAid.aid === identifier.prefix
        )

        let type
        if (!foundInAgent) {
          const oobi = await getOobis(signifyClient, identifier.name, 'agent')
          if (!oobi.oobis || !oobi.oobis.length) {
            // console.warn(
            //   'OOBI not present for this identifier',
            //   identifier.name
            // )
            continue
          }
          const oobiMultisig = oobi.oobis[0].split('/agent/')[0]
          type = getIdentifierTypeFromAlias(identifier.name, userId)

          let threshold
          if (typeof identifier?.raw?.state?.kt === 'string') {
            threshold = identifier?.raw?.state?.kt
          } else threshold = JSON.stringify(identifier?.raw?.state?.kt)

          // identifier.group is optional on the multisig DTO; the
          // multisigKeriaIdentifiers filter above doesn't statically narrow it,
          // so guard before the .members map.
          const memberAIDs =
            identifier.group?.members?.map((member) => member.aid) ?? []

          const payload: CreateMultisigRequest = {
            multisigAid: {
              aid: identifier.prefix,
              alias: identifier.name,
              oobi: [oobiMultisig],
              userId,
              isMultisig: identifier.isMultisig,
              orgId: primaryOrgId,
              type: type,
              signingThreshold: threshold
            },
            memberAIDs
          }

          try {
            await saveMultisigToAgent(payload)
          } catch (err) {
            // console.log(err)
          }
        } else {
          type = foundInAgent.type
        }
        const updatedIdentifier = {
          ...identifier,
          type: type
        }
        updateIdentifier(updatedIdentifier)
      }

      //console.debug('[useKeriaAgentSync] sync:completed')
      setStatus(SyncStatus.COMPLETED)
    } catch (err) {
      //console.error('[useKeriaAgentSync] sync:failed', err)
      setStatus(SyncStatus.FAILED)
      setError(err)
    }
  }

  useEffect(() => {
    if (
      clientConnectivityStatus !== CLIENT_CONNECTIVITY_STATUS.CONNECTED ||
      notificationProcessingStatus.length > 0 ||
      status === SyncStatus.SYNCING
    )
      return
    sync()
    // reason: sync is a local async writing to status; adding status/sync as deps would loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientConnectivityStatus, notificationProcessingStatus])

  return { status, error, sync }
}

export default useKeriaAgentSync
